<template>
    <div class="h-full w-full items-center">
        <div
            class="relative w-full"
            :style="{ aspectRatio: currentAspectRatio }"
        >
            <div
                v-if="isDecorative"
                ref="playerEl"
                aria-hidden="true"
                tabindex="-1"
                class="absolute inset-0 [&_iframe]:!h-full [&_iframe]:!w-full"
            />
            <template v-else>
                <iframe v-lazy-load 
                    v-if="showDesktop"
                    :data-src="desktopEmbedUrl"
                    style="position:absolute;inset:0;width:100%;height:100%;"
                    frameborder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowfullscreen
                />
                <iframe v-lazy-load 
                    v-else-if="showMobile"
                    :data-src="mobileEmbedUrl"
                    style="position:absolute;inset:0;width:100%;height:100%;"
                    frameborder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowfullscreen
                />
            </template>

            <Transition
                enter-active-class="transition-opacity duration-300"
                enter-from-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="transition-opacity duration-200"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <button
                    v-if="isDecorative && videoEnded"
                    class="absolute bottom-4 right-4 flex h-8 w-11 items-center justify-center rounded bg-[#00603B] text-white shadow-lg transition-transform focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#00603B] hover:scale-105"
                    :aria-label="t('slots.video.playAriaLabel')"
                    type="button"
                    @click="replay"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        class="h-6 w-6"
                        aria-hidden="true"
                    >
                        <path d="M8 5v14l11-7z" />
                    </svg>
                </button>
            </Transition>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useMediaQuery } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import type Player from '@vimeo/player'
import type { HblSlot } from '@/utils/types'

interface VimeoOEmbed {
    width: number
    height: number
}

const { t } = useI18n()

const props = defineProps<{
    content: HblSlot
}>()

const config = computed(() => props.content?.config)

function toAspectRatio(meta: VimeoOEmbed | null): string {
    return meta ? `${meta.width} / ${meta.height}` : '16 / 9'
}

function embedUrl(id: string): string {
    return `https://player.vimeo.com/video/${id}`
}

const desktopId = config.value?.videoUrl?.value ?? ''
const mobileId = config.value?.videoUrlMobile?.value ?? ''
const isDecorative = computed(() => !!config.value?.decorative?.value)

const { data: desktopMeta } = useAsyncData<VimeoOEmbed | null>(
    `vimeo-ar-${desktopId}`,
    () => desktopId
        ? $fetch<VimeoOEmbed>(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${desktopId}`)
        : Promise.resolve(null)
)

const { data: mobileMeta } = useAsyncData<VimeoOEmbed | null>(
    `vimeo-ar-${mobileId}`,
    () => mobileId
        ? $fetch<VimeoOEmbed>(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${mobileId}`)
        : Promise.resolve(null)
)

const isMobile = useMediaQuery('(max-width: 767px)')
const hasMobileVideo = computed(() => mobileId.length > 0)

const showDesktop = computed(() => !isMobile.value || !hasMobileVideo.value)
const showMobile = computed(() => isMobile.value && hasMobileVideo.value)

const currentAspectRatio = computed(() =>
    showMobile.value ? toAspectRatio(mobileMeta.value) : toAspectRatio(desktopMeta.value)
)

const desktopEmbedUrl = computed(() => embedUrl(desktopId))
const mobileEmbedUrl = computed(() => embedUrl(mobileId))

// Decorative player: autoplay once, no loop, show play button after end
const playerEl = ref<HTMLElement | null>(null)
const videoEnded = ref(false)
let player: Player | null = null

onMounted(async () => {
    if (!isDecorative.value || !playerEl.value) return

    const videoId = showMobile.value && hasMobileVideo.value ? mobileId : desktopId
    if (!videoId) return

    const { default: VimeoPlayer } = await import('@vimeo/player')

    player = new VimeoPlayer(playerEl.value, {
        id: Number(videoId),
        autoplay: true,
        muted: true,
        controls: false,
        loop: false,
        title: false,
        byline: false,
        portrait: false,
        transparent: true,
    })

    player.on('ended', () => {
        videoEnded.value = true
    })
})

onBeforeUnmount(() => {
    player?.destroy()
    player = null
})

async function replay(): Promise<void> {
    if (!player) return
    videoEnded.value = false
    await player.play()
}
</script>
